import React from "react";
import { useNavigate } from "react-router-dom";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
} from "recharts";

interface DashboardChartCardProps {
  title: string;
  icon: React.ReactNode;
  color?: string;
  data: any[];
  xKey: string;
  yKey: string;
  navigateTo: string;
  tooltipFormatter?: (value: any) => string | [string, string];
  labelFormatter?: (label: any) => string;
  tickFormatter?: (tick: any) => string;
}

const tailwindColors: Record<string, string> = {
  blue: "#3b82f6",
  green: "#22c55e",
  red: "#ef4444",
  yellow: "#eab308",
  purple: "#8b5cf6",
  pink: "#ec4899",
  orange: "#f97316",
  teal: "#14b8a6",
  indigo: "#6366f1",
  gray: "#6b7280",
};

const DashboardChartCard: React.FC<DashboardChartCardProps> = ({
  title,
  icon,
  color = "blue",
  data,
  xKey,
  yKey,
  navigateTo,
  tooltipFormatter,
  labelFormatter,
  tickFormatter,
}) => {
  const navigate = useNavigate();

  // Dynamically pick stroke color or fallback to blue
  const stroke = tailwindColors[color] || tailwindColors.blue;
  const border = `border-${color}-200 dark:border-${color}-800`;
  const text = `text-${color}-900 dark:text-${color}-300`;

  // Default tick formatter for dates (yyyy-mm-dd → dd-mm-yyyy)
  const defaultTickFormatter = (tick: string) => {
    if (!tick) return "";
    try {
      const [year, month, day] = tick.split("-");
      return `${day}-${month}-${year}`;
    } catch {
      return tick;
    }
  };

  // Default label formatter for tooltip
  const defaultLabelFormatter = (label: string) => {
    if (!label) return "";
    try {
      const [year, month, day] = label.split("-");
      return `${day}-${month}-${year}`;
    } catch {
      return label;
    }
  };

  return (
    <div
      className={`relative bg-white dark:bg-gray-900/70 rounded-xl shadow-md backdrop-blur-md ${border} p-4 hover:shadow-lg transition duration-300 ease-in-out transform cursor-pointer`}
    >
      {/* Header */}
      <div className={`flex justify-between items-center mb-4 border-b pb-2 ${border}`}>
        <h2 className={`text-lg font-semibold flex items-center space-x-2 ${text}`}>
          {icon}
          <span>{title}</span>
        </h2>
        <button
          type="button"
          onClick={() => navigate(navigateTo)}
          className={`text-xs font-medium ${text} hover:opacity-80 transition`}
        >
          View All
        </button>

      </div>

      {/* Chart */}
      <ResponsiveContainer width="100%" height={160}>
        <LineChart data={data}>
          <defs>
            <linearGradient id={`gradient-${color}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={stroke} stopOpacity={0.4} />
              <stop offset="95%" stopColor={stroke} stopOpacity={0.05} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="5 5" className="stroke-gray-300 dark:stroke-gray-600" />
          <XAxis
            dataKey={xKey}
            tick={{ fill: "#334155", fontSize: 10, fontWeight: "600" }}
            axisLine={{ stroke: "#94a3b8" }}
            tickFormatter={tickFormatter || defaultTickFormatter}
          />
          <YAxis
            tick={{ fill: "#334155", fontSize: 10, fontWeight: "600" }}
            axisLine={{ stroke: "#94a3b8" }}
            allowDecimals={false}
          />
          <Tooltip
            contentStyle={{ backgroundColor: "#f1f5f9" }}
            itemStyle={{ color: stroke }}
            formatter={tooltipFormatter}
            labelFormatter={labelFormatter || defaultLabelFormatter}
          />
          <Area
            type="monotone"
            dataKey={yKey}
            stroke="none"
            fill={`url(#gradient-${color})`}
            isAnimationActive
            animationDuration={1200}
          />
          <Line
            type="monotone"
            dataKey={yKey}
            stroke={stroke}
            strokeWidth={2}
            isAnimationActive
            animationDuration={1200}
            dot={{ r: 4, fill: stroke }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default DashboardChartCard;
